import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { merge, assign } from 'lodash';
import { InferType, object, string, number, boolean } from 'yup';
import i18n from '@components/i18n';
import { InsuranceTypesEnum, tInsuranceTypeEnum } from '@models/InsuranceType';
import { PaymentMethods } from '@models/PaymentInfo';
import { selectCurrentUser, selectIsHospital } from '@components/Auth';
import { selectHealthPolicyClaim } from './selector';
import { FormValues, formValuesSchema } from './schema';

export const useInitialValues = () => {
  const { policy, insurancePolicyUser } = useSelector(selectHealthPolicyClaim);
  const currentUser = useSelector(selectCurrentUser);
  const isHospital = useSelector(selectIsHospital);
  const validationSchema = useFormValuesSchema();
  const initialValues: FormValues = validationSchema.getDefault() as unknown as FormValues;

  return useMemo(() => {
    merge(initialValues, {
      coveredByOtherInsurance: '',
      totalClaimedAmount: '',
      policyHolder: insurancePolicyUser?.user?.name ?? '',
      policyHolderDob: insurancePolicyUser?.user?.dob ?? '',
      policyHolderGender: insurancePolicyUser?.user?.gender ?? '',
      policyNumber: policy?.policyNumber ?? '',
      policyPlanType: `${tInsuranceTypeEnum((policy?.insuranceType as InsuranceTypesEnum) ?? '')} - ${insurancePolicyUser?.benefitTemplate.name ?? ''}`,
      policyExpiryDate: insurancePolicyUser?.expirationDate ?? '',
      mobileNumber: isHospital ? currentUser.mobileNumber : (insurancePolicyUser?.user?.mobileNumber ?? ''),
      email: isHospital ? currentUser.email : (insurancePolicyUser?.user?.email ?? ''),
      businessPartnerId: isHospital ? currentUser.businessPartner.id : '',
      paymentInfo: {
        beneficiaryName: isHospital ? currentUser.businessPartner.name : (insurancePolicyUser?.user?.name ?? ''),
        paymentMethod: PaymentMethods.BankTransfer,
        bankName: isHospital ? currentUser.businessPartner.bankName : '',
        bankAccount: isHospital ? currentUser.businessPartner.bankAccountNumber : '',
      },
    });

    return initialValues;
  }, [policy, insurancePolicyUser]);
};

export const useFormValuesSchema = () => {
  const isHospital = useSelector(selectIsHospital);
  let schema = assign(formValuesSchema, {});

  if (isHospital) {
    schema.omit(['businessPartnerId']).concat(
      object().shape({
        businessPartnerId: string().default('').required(i18n.t('validation:required'))
      })
    );
  } else {
    schema.omit(['hospitalName']).concat(
      object().shape({
        hospitalName: string().default('').required(i18n.t('validation:required'))
      })
    )
  }

  return schema;
}
